import { useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import PanelsMenu from "../menus/PanelsMenu";
import LoadingSpinner from "../common/LoadingSpinner";
import axios from "axios";
import "../../styles/components/FilterModal.css";
import panels from "../../configs/panels.json";
import GeneNamesForm from "../forms/GeneNamesForm";

const PanelsModal = ({
  showModal,
  handleClose,
  selectedPanels,
  setSelectedPanels,
  filterData,
  setFilterData,
}) => {
  const [showGeneNames, setShowGeneNames] = useState(false); // Control whether to show gene names
  const [editedGeneNames, setEditedGeneNames] = useState(""); // Store the edited gene names
  const [loading, setLoading] = useState(false); // Loading state for Apply button

  // Use geneNames and to update the filterData
  const handleAddGeneNames = () => {
    let temp = { ...filterData };
    if (editedGeneNames.length > 0) {
      temp["feature"] = temp["feature"]
        ? temp["feature"] + "," + editedGeneNames
        : editedGeneNames;
      setFilterData(temp);

      // Reset the editedGeneNames
      setEditedGeneNames("");
    }
    setShowGeneNames(false);
    handleClose();
  };

  const ApplyPanels = useCallback(async () => {
    const PanelIDs = selectedPanels.map((item) => item.id);

    setLoading(true); // Start loading

    try {
      const geneNamesSet = new Set(); // To store unique gene names

      // Create an array of promises, one for each panel ID
      const requests = PanelIDs.map((panelId) =>
        axios.get(
          `https://panelapp.genomicsengland.co.uk/api/v1/panels/${panelId}/`,
          {
            headers: {
              accept: "application/json",
              // Add CSRF token or authentication if required
              "X-CSRFToken": "YOUR_CSRF_TOKEN_HERE",
            },
          }
        )
      );

      // Wait for all requests to complete
      const responses = await Promise.all(requests);

      // Process each response to extract gene names
      responses.forEach((response) => {
        const geneData = response.data.genes; // Assuming 'genes' is the field containing gene details
        // console.log("geneData", geneData);
        const panelGeneNames = geneData.map(
          (gene) => gene.gene_data.gene_symbol
        ); // Assuming 'gene_symbol' is the key for the gene names
        // console.log("panelGeneNames", panelGeneNames);
        // Add gene names to the Set to automatically handle duplicates
        panelGeneNames.forEach((name) => geneNamesSet.add(name));
      });

      // console.log("geneNamesSet", geneNamesSet);

      // Convert Set to array and join names with a comma
      const geneNames = Array.from(geneNamesSet);

      // Update editedGeneNames with the unique gene names
      setEditedGeneNames(geneNames.join(", "));

      // Switch to showing gene names inside the modal
      setShowGeneNames(true);
    } catch (error) {
      console.error("Failed to apply panels:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  }, [selectedPanels]);

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="lg"
      className="filter-modal bounce-in"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showGeneNames ? "Gene Names from Selected Panels" : "Select Panels"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filter-modal-body">
          {showGeneNames ? (
            // Show editable gene names with loading state
            <GeneNamesForm
              editedGeneNames={editedGeneNames}
              setEditedGeneNames={setEditedGeneNames}
              loading={loading}
            />
          ) : (
            // Show the disease selection menu, disable during loading
            <PanelsMenu
              data={panels}
              selectedPanels={selectedPanels}
              setSelectedPanels={setSelectedPanels}
              disable={loading} // Disable HierarchicalMenu when loading
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {showGeneNames ? (
          // If showing gene names, allow user to go back or add gene names
          <>
            <Button
              variant="success"
              className="btn-animate"
              onClick={handleAddGeneNames}
              disabled={loading} // Disable Add button when loading
            >
              Add
            </Button>
            <Button
              variant="primary"
              className="btn-animate"
              onClick={() => setShowGeneNames(false)}
              disabled={loading} // Disable Back button when loading
            >
              Back
            </Button>
          </>
        ) : (
          // If showing disease selection, show Apply and Close buttons
          <>
            <Button
              variant="primary"
              className="btn-animate d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                ApplyPanels();
              }}
              disabled={loading || selectedPanels.length === 0} // Disable when loading or conditions not met
              title={
                selectedPanels.length === 0
                  ? "Please select diseases and datasets first"
                  : ""
              }
            >
              {loading ? (
                <>
                  <LoadingSpinner /> Applying...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PanelsModal;
